/**
 * Frido Swatch Selector runtime.
 *
 * Per-value swatch resolution, in priority order: authored per-value swatch
 * image (value_display.image) -> image tile; native Shopify swatch color/image;
 * authored per-value hex (value_display.color) -> a two-hex pair "#a,#b" renders
 * a two-tone SPLIT chip (Figma Component 8), a single hex a solid chip; variant
 * featured image -> image; else neutral grey. Image full-bleeds the tile; solid
 * and split chips are the inset 40×40. Picking a swatch selects the variant.
 */
;(() => {
  if (window.__sai_fswfrd01_initialized__) return
  window.__sai_fswfrd01_initialized__ = true

  const SNIPPET_ID = 'fswfrd01'
  const TAG = 'sai-fswfrd01'
  const FEATURE_SLUG = 'variant_picker'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }


  const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

  // A per-variant badge pill (value_display badges: { label, bg, fg, icon }). Icon is
  // either a URL (rendered as <img>) or a short glyph/emoji. Missing label -> no
  // pill. Colours are authored data, so they're written into inline style with
  // esc() guarding attribute breakout.
  function badgePill(b) {
    if (!b || !b.label) return ''
    const bg = b.bg || '#000000'
    const fg = b.fg || '#ffffff'
    const icon = b.icon
      ? (/^(https?:)?\/\//.test(b.icon)
          ? `<img src="${esc(sizeSwatchCdn(b.icon))}" alt="" loading="lazy" decoding="async">`
          : `<span aria-hidden="true">${esc(b.icon)}</span>`)
      : ''
    return `<span class="sai-fswfrd01__badge" style="background:${esc(bg)};color:${esc(fg)}">${icon}${esc(b.label)}</span>`
  }
  function badgesFor(pool, value) {
    const list = (pool.product.variant_badges || {})[value]
    return Array.isArray(list) ? list : []
  }

  // Image-only resolution: authored per-value swatch image, then native swatch
  // image, then the variant's featured photo. Returns null when no image exists.
  const sizeSwatchCdn = (u) => { if (!u || typeof u !== 'string') return u; if (!/\/cdn\/shop\/|cdn\.shopify\.com/.test(u)) return u; return /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, '$1width=200') : u + (u.includes('?') ? '&' : '?') + 'width=200'; }
function swatchImage(pool, value) {
    const simg = (pool.product.swatch_images || {})[value]
    if (typeof simg === 'string' && /^(https?:)?\/\//.test(simg)) return { kind: 'image', src: simg }
    const nat = (pool.product.swatches || {})[value]
    if (nat && nat.image) return { kind: 'image', src: nat.image }
    const vimg = (pool.product.value_images || {})[value]
    if (vimg) return { kind: 'image', src: vimg }
    return null
  }
  // Chip-only resolution: authored per-value hex (value_display.color) — a
  // comma/slash pair "#a,#b" is a two-tone SPLIT chip (Figma Component 8), a
  // single hex a solid fill — then the native option-value swatch colour.
  // Returns null when no colour exists.
  function swatchChip(pool, value) {
    const hex = (pool.product.swatch_hex || {})[value]
    if (typeof hex === 'string') {
      const parts = hex.split(/[,/]/).map((h) => h.trim()).filter(Boolean)
      if (parts.length >= 2 && HEX_RE.test(parts[0]) && HEX_RE.test(parts[1])) return { kind: 'split', a: parts[0], b: parts[1] }
      if (parts.length === 1 && HEX_RE.test(parts[0])) return { kind: 'solid', a: parts[0] }
    }
    const nat = (pool.product.swatches || {})[value]
    if (nat && typeof nat.color === 'string' && HEX_RE.test(nat.color)) return { kind: 'solid', a: nat.color }
    return null
  }
  const GREY = { kind: 'solid', a: '#d9d9d9' }

  // `prefer` is the placement's `swatch_display` choice:
  //   'image' -> force a product/swatch image (chip only if no image resolvable)
  //   'chip'  -> force a hex/native colour chip (image only if no colour)
  //   'auto'  -> the original data-driven priority (image > native colour >
  //              native image > authored hex > variant photo > neutral grey).
  // Each forced mode falls back to the other kind before the neutral grey, so a
  // value missing the preferred source degrades visibly instead of blank.
  function swatchFor(pool, value, prefer) {
    if (prefer === 'image') return swatchImage(pool, value) || swatchChip(pool, value) || GREY
    if (prefer === 'chip') return swatchChip(pool, value) || swatchImage(pool, value) || GREY
    // auto — original priority, preserved exactly
    const simg = (pool.product.swatch_images || {})[value]
    if (typeof simg === 'string' && /^(https?:)?\/\//.test(simg)) return { kind: 'image', src: simg }
    const nat = (pool.product.swatches || {})[value]
    if (nat) {
      if (typeof nat.color === 'string' && HEX_RE.test(nat.color)) return { kind: 'solid', a: nat.color }
      if (nat.image) return { kind: 'image', src: nat.image }
    }
    const hex = (pool.product.swatch_hex || {})[value]
    if (typeof hex === 'string') {
      const parts = hex.split(/[,/]/).map((h) => h.trim()).filter(Boolean)
      if (parts.length >= 2 && HEX_RE.test(parts[0]) && HEX_RE.test(parts[1])) return { kind: 'split', a: parts[0], b: parts[1] }
      if (parts.length === 1 && HEX_RE.test(parts[0])) return { kind: 'solid', a: parts[0] }
    }
    const img = (pool.product.value_images || {})[value]
    if (img) return { kind: 'image', src: img }
    return GREY
  }

  function optKey(opt) { return 'option' + (opt ? opt.position : 1) }

  // ---- Merchant-hidden values (custom.variantHide) --------------------------
  // The flag lives on the variant but means "drop this value from the picker",
  // so a value disappears only when every variant carrying it is flagged. A
  // partially-flagged value still renders and merely loses those variants as
  // choices — which is why hidden variants are filtered out of variantsFor
  // rather than handled at the value level alone: it keeps availability,
  // resolution and the rendered set agreeing on one definition of selectable.
  //
  // Editorial, not stock-derived: a sold-out value stays visible (struck) and a
  // hidden one disappears whether or not stock remains.
  function hiddenValues(pool) {
    const P = (pool && pool.product) || {}
    const key = optKey(P.color_option)
    const vals = (P.color_option && P.color_option.values) || []
    const set = {}
    for (const val of vals) {
      const vs = (P.variants || []).filter((v) => v[key] === val)
      if (vs.length && vs.every((v) => v.hidden === true)) set[val] = true
    }
    return set
  }

  function variantsFor(pool, value, constraints) {
    const key = optKey(pool.product.color_option)
    return (pool.product.variants || []).filter((v) =>
      v[key] === value
      && v.hidden !== true
      && (!constraints || Object.keys(constraints).every((k) => v[k] === constraints[k])))
  }
  function isAvailable(pool, value, constraints) {
    return variantsFor(pool, value, constraints).some((v) => v.available)
  }
  function pickVariant(pool, value, curId, constraints, freeKeys) {
    const vs = variantsFor(pool, value, constraints)
    // Axes below this one move only when they must: keep the shopper's existing
    // size while it is still sellable in the new colour, otherwise advance to
    // one that is, so a pick can never land on a dead combination the picker
    // showed as fine.
    const cur = curId != null ? (pool.product.variants || []).find((v) => String(v.id) === String(curId)) : null
    const keeps = (v) => !cur || !freeKeys || freeKeys.every((k) => cur[k] == null || v[k] === cur[k])
    return vs.find((v) => v.available && keeps(v)) || vs.find((v) => v.available)
      || vs.find(keeps) || vs[0] || null
  }

  // ---- Top-down axis cascade ------------------------------------------------
  // A picker judges availability against the axes ABOVE it on the page, never
  // the ones below. The first picker a shopper reads therefore shows every
  // value it can still sell in some combination, and each picker beneath it
  // narrows to the choice already made. Which axis is "on top" is read from the
  // DOM, so re-ordering the blocks in a template flips the cascade with no code
  // change and the two axes can never disagree about what is in stock.
  //
  // An axis with no picker of its own is always pinned — nothing on the page
  // can change it, so it constrains everything.
  //
  // Pickers are grouped by their nearest <dialog> ancestor so the mini-PDP's
  // copy of a picker never constrains the one in the buybox.
  const AXIS_PICKERS = 'sai-fszfrd01[data-sai-axis],sai-fswfrd01[data-sai-axis]'
  function axisGroup(el) { return el.closest('dialog') || document }
  function axisKeysBelow(el) {
    const group = axisGroup(el)
    const keys = []
    for (const p of group.querySelectorAll(AXIS_PICKERS)) {
      if (p === el || axisGroup(p) !== group) continue
      if (!(el.compareDocumentPosition(p) & Node.DOCUMENT_POSITION_FOLLOWING)) continue
      keys.push(p.getAttribute('data-sai-axis'))
    }
    return keys
  }

  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
      this._curVariantId = null
      // Another axis snippet (e.g. size) selecting a variant must update our
      // notion of the current variant so our colour picks preserve that size.
      // Our own picks set _curVariantId first (see _pick), so this is a no-op
      // for events we fired. Also adopt the new variant's colour so the swatch
      // highlight stays in sync when the size snippet resolves a variant.
      this._onVariantChange = (e) => {
        const id = e && e.detail && e.detail.variantId
        if (id == null || String(id) === String(this._curVariantId) || !this._pool) return
        this._curVariantId = id
        const p = this._pool.product
        const nv = p && (p.variants || []).find((v) => String(v.id) === String(id))
        if (nv && p.color_option) {
          const ck = optKey(p.color_option)
          if (nv[ck] != null) this._selected = nv[ck]
          const snapped = this._normalizeSelected()
          // Repaint even when the colour itself did not move: a change on an
          // axis above this one changes which colours are still sellable, and
          // that is exactly the strike state this row is showing.
          this._render()
          // Another picker resolved onto a hidden value. Re-commit so the page
          // follows the value we snapped to; the resulting event carries a
          // visible variant, so this settles in one extra round rather than
          // bouncing between the two pickers.
          if (snapped) this._commitSelection(this._selected)
        }
      }
      window.addEventListener('spectrum:variant-change', this._onVariantChange)
    }
    disconnectedCallback() {
      if (this._onVariantChange) window.removeEventListener('spectrum:variant-change', this._onVariantChange)
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      const p = this._pool.product
      if (!p || !p.color_option) return
      this._selected = p.color_option.selected || null
      const snapped = this._normalizeSelected()
      if (this._curVariantId == null && p.current_variant_id != null) this._curVariantId = p.current_variant_id
      this._render()
      this._bind()
      if (snapped) this._commitSelection(this._selected)
    }

    _adopt(pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      const p = this._pool.product
      if (!p || !p.color_option) return false
      if (!this.querySelector('.sai-fswfrd01__inner')) return false
      const sel = this.querySelector('.sai-fswfrd01__sw.is-sel, .sai-fswfrd01__pill.is-sel')
      this._selected = (sel && sel.dataset.value) || p.color_option.selected || null
      const snapped = this._normalizeSelected()
      if (this._curVariantId == null && p.current_variant_id != null) this._curVariantId = p.current_variant_id
      this._painted = true
      // The SSR row judges availability on any-variant stock, because Liquid
      // cannot see which pickers render above this one. That is already right
      // when nothing constrains this axis; when something does, the SSR row
      // overstates what is in stock and has to be repainted before the shopper
      // can act on it. Repaint is layout-neutral — same markup, only the is-oos
      // classes differ — so the CLS reserve the SSR row buys is not spent.
      if (snapped || Object.keys(this._constraints()).length) this._render()
      this._bind()
      if (snapped) this._commitSelection(this._selected)
      return true
    }

    _props() { return (this._pool && this._pool.props) || {} }

    // Values the merchant has hidden, and the set actually offered. Recomputed
    // per call rather than cached — it is a handful of values against a handful
    // of variants, and a stale cache here would render a value that must not be
    // sellable.
    _hidden() { return hiddenValues(this._pool) }

    _visibleValues() {
      const opt = this._pool.product.color_option
      const hidden = this._hidden()
      return ((opt && opt.values) || []).filter((v) => !hidden[v])
    }

    // Shopify resolves the initial variant with no knowledge of the flag (a
    // shared link to a hidden variant is enough), and a sibling picker can hand
    // us one too, so any adopted selection is snapped back to a visible value
    // before it reaches the render. Returns true when it moved — callers must
    // then commit, or the row would show one value while the cart still holds
    // the hidden variant.
    _normalizeSelected() {
      if (!this._selected || !this._hidden()[this._selected]) return false
      const next = this._visibleValues()[0] || null
      if (next === this._selected) return false
      this._selected = next
      return true
    }

    // Resolve `value` to a variant and make it the page's selection: URL,
    // shared variant-change event, and the theme form's hidden input (which is
    // what add-to-cart actually submits). Returns the variant so callers can
    // layer UI on top. Separate from _pick because the picker also commits
    // selections it made on the shopper's behalf, where _pick's scroll and
    // analytics would be wrong.
    _commitSelection(value) {
      const variant = pickVariant(
        this._pool, value, this._curVariantId, this._constraints(), this._freeKeys())
      if (!variant) return null
      // mark first so our own variant-change listener treats this as a no-op
      this._curVariantId = variant.id
      try {
        const url = new URL(window.location.href)
        url.searchParams.set('variant', String(variant.id))
        window.history.replaceState({}, '', url.toString())
      } catch (e) {}
      window.dispatchEvent(new CustomEvent('spectrum:variant-change', { detail: { variantId: variant.id } }))
      // Scope to forms whose current value is one of THIS product's variants so
      // upsell forms for other products are never touched.
      try {
        const _vids = {}
        ;(this._pool.product.variants || []).forEach((vv) => { _vids[String(vv.id)] = 1 })
        document.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
          if (_vids[String(input.value)]) {
            input.value = String(variant.id)
            input.dispatchEvent(new Event('change', { bubbles: true }))
          }
        })
      } catch (e) {}
      return variant
    }

    _currentVariant() {
      const P = this._pool && this._pool.product
      if (!P) return null
      const id = this._curVariantId != null ? this._curVariantId : P.current_variant_id
      return (P.variants || []).find((v) => String(v.id) === String(id)) || null
    }

    // Axes owned by a picker rendered below this one. They stay unpinned so this
    // picker reports everything it can still sell (see the cascade note above).
    _freeKeys() { return axisKeysBelow(this) }

    // The axes this picker must hold fixed: every other axis except the ones a
    // picker below owns.
    _constraints() {
      const P = this._pool.product
      const key = optKey(P.color_option)
      const free = this._freeKeys()
      const cur = this._currentVariant()
      const out = {}
      if (cur) {
        for (const k of ['option1', 'option2', 'option3']) {
          if (k === key || free.indexOf(k) !== -1) continue
          if (cur[k] != null) out[k] = cur[k]
        }
      }
      return out
    }

    // Live decides swatch-vs-pill by the option's displayConf.kind: ASSET (a
    // dedicated swatch IMAGE) -> image swatches; COLOR / null -> text pills.
    // We derive it from data: an axis renders image swatches only when a value
    // carries a dedicated swatch image (custom.swatch_image or native swatch
    // image). Hex and the variant's featured product photo do NOT count — live
    // shows text pills for those, so we must too.
    _axisHasImage(values) {
      const p = this._pool.product
      return values.some((v) => {
        const simg = (p.swatch_images || {})[v]
        if (typeof simg === 'string' && /^(https?:)?\/\//.test(simg)) return true
        const nat = (p.swatches || {})[v]
        return !!(nat && nat.image)
      })
    }

    _patchInPlace(values, imageMode, oosMode, constraints) {
      if (oosMode === 'hide') return false
      if (!this.querySelector('.sai-fswfrd01__inner')) return false
      if (!!this.querySelector('.sai-fswfrd01__row') !== imageMode) return false
      const btns = this.querySelectorAll('.sai-fswfrd01__sw, .sai-fswfrd01__pill')
      if (btns.length !== values.length) return false
      for (let i = 0; i < values.length; i++) if (btns[i].dataset.value !== values[i]) return false
      // A swatch's KIND (image vs solid/split chip) can change while the value set
      // stays identical — swatch_display flips auto->chip, or an authored hex/image
      // is added/removed. In-place patching only updates classes, never the tile
      // content, so a kind change would strand the stale tile. Detect it and fall
      // through to a full rebuild; steady-state (no kind change) still patches.
      if (imageMode) {
        const displayMode = this._props().swatch_display || 'auto'
        for (let i = 0; i < values.length; i++) {
          const want = swatchFor(this._pool, values[i], displayMode)
          const chip = btns[i].querySelector('.chip')
          const have = btns[i].querySelector('img') ? 'image' : (chip ? (chip.children.length >= 2 ? 'split' : 'solid') : 'none')
          if (want.kind !== have) return false
        }
      }
      for (let i = 0; i < values.length; i++) {
        const v = values[i]
        const b = btns[i]
        const avail = isAvailable(this._pool, v, constraints)
        const disabled = !avail && oosMode === 'disable'
        b.classList.toggle('is-sel', v === this._selected)
        b.classList.toggle('is-oos', !avail)
        b.classList.toggle('is-disabled', disabled)
        if (disabled) { b.setAttribute('aria-disabled', 'true'); b.removeAttribute('data-action') }
        else { b.removeAttribute('aria-disabled'); b.setAttribute('data-action', 'pick') }
      }
      const lv = this.querySelector('[data-sel-label]')
      if (lv) lv.textContent = this._selected || ''
      return true
    }

    _render() {
      const props = this._props()
      const opt = this._pool.product.color_option
      const showVal = props.show_selected_in_label !== false
      const showLabel = props.show_label !== false
      // label default = the rendered axis's own name, uppercased ("KEY :" —
      // the Figma heading grammar). show_label:false omits the label (bare swatches).
      const label = showLabel
        ? `<div class="sai-fswfrd01__label"><span class="sai-fswfrd01__label-key">${esc(props.label_prefix || (opt.name || 'Color').toUpperCase() + ' :')}</span>${showVal ? `<span class="sai-fswfrd01__label-val" data-sel-label>${esc(this._selected || '')}</span>` : ''}</div>`
        : ''

      // `swatch_display` config forces the presentation; 'auto' keeps the
      // data-driven image-vs-pill decision. 'image' and 'chip' both render the
      // tile container (chips ride inside the tile as an inset fill). 'pill'
      // forces the bordered text-pill layout for every value regardless of
      // authored images/colours (matches storefronts that show descriptive
      // text options even when swatch art exists). 'auto' shows pills only when
      // no value carries a swatch image.
      const displayMode = props.swatch_display || 'auto'
      // Hidden values are excluded before the image-vs-pill decision too, or a
      // hidden value's swatch image could flip the whole row into image mode.
      const values = this._visibleValues()
      const imageMode =
        displayMode === 'auto' ? this._axisHasImage(values)
          : displayMode === 'pill' ? false
            : true
      let row = imageMode
        ? `<div class="sai-fswfrd01__row" data-shape="${esc(props.shape || 'square')}">`
        : '<div class="sai-fswfrd01__pills">'
      // oos_display prop (Studio): strike (default) | hide | disable — the liquid SSR mirrors this.
      const oosMode = props.oos_display || 'strike'
      const constraints = this._constraints()
      // Repaint in place when only the selection/is-oos classes + label change (a
      // constraint from an axis above, or a pick). Replacing innerHTML recreates
      // the <img> swatch tiles, which reload and flicker; patching keeps them.
      // Falls through to the full rebuild below only when the button set changes.
      if (this._patchInPlace(values, imageMode, oosMode, constraints)) return
      for (const v of values) {
        // Struck means "not sellable given the axes above this picker". With no
        // picker above, that is full sold-out (any size) and matches the SSR
        // _avail exactly; with one above, this is the pass that corrects the
        // SSR's optimistic guess.
        const avail = isAvailable(this._pool, v, constraints)
        if (!avail && oosMode === 'hide') continue
        const disabled = !avail && oosMode === 'disable'
        const badges = badgesFor(this._pool, v)
        const badgeHtml = this._props().show_badges !== false && badges.length
          ? `<span class="sai-fswfrd01__badges">${badges.map(badgePill).join('')}</span>`
          : ''
        if (imageMode) {
          const sw = swatchFor(this._pool, v, displayMode)
          const cls = ['sai-fswfrd01__sw', v === this._selected ? 'is-sel' : '', avail ? '' : 'is-oos', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')
          let inner = ''
          if (sw.kind === 'image') inner = `<img src="${esc(sizeSwatchCdn(sw.src))}" alt="${esc(v)}" loading="lazy" decoding="async">`
          else if (sw.kind === 'split') inner = `<span class="chip"><span style="background:${esc(sw.a)}"></span><span style="background:${esc(sw.b)}"></span></span>`
          else inner = `<span class="chip"><span style="background:${esc(sw.a)}"></span></span>`
          // badge lives in the cell (overflow visible), not the tile (overflow hidden)
          row += `<span class="sai-fswfrd01__cell">${badgeHtml}<button type="button" class="${cls}"${disabled ? ' aria-disabled="true"' : ' data-action="pick"'} data-value="${esc(v)}" title="${esc(v)}">${inner}</button></span>`
        } else {
          const cls = ['sai-fswfrd01__pill', v === this._selected ? 'is-sel' : '', avail ? '' : 'is-oos', disabled ? 'is-disabled' : ''].filter(Boolean).join(' ')
          row += `<span class="sai-fswfrd01__cell">${badgeHtml}<button type="button" class="${cls}"${disabled ? ' aria-disabled="true"' : ' data-action="pick"'} data-value="${esc(v)}" title="${esc(v)}">${esc(v)}</button></span>`
        }
      }
      row += '</div>'

      const align = props.swatch_align === 'center' ? 'center' : 'start'
      this.innerHTML = `<div class="sai-fswfrd01__inner" data-align="${align}">${label}${row}</div>`
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action="pick"]')
        if (!t) return
        this._pick(t.dataset.value)
      })
    }

    _pick(value) {
      // A fully out-of-stock colour stays selectable (same as the size picker):
      // picking it selects its unavailable variant so Add-to-Cart surfaces OUT
      // OF STOCK, instead of swallowing the tap and trapping the shopper.
      this._selected = value
      this._render()
      const variant = this._commitSelection(value)
      if (variant) {
        // After an IN-STOCK colour change, bring the product gallery back into
        // view so the shopper sees the new variant's image. Gated on the resolved
        // variant's availability: picking a swatch that is out of stock for the
        // current selection still selects it (so ATC can show OUT OF STOCK) but
        // must NOT scroll. Gallery selector leads with the active gallery snippet
        // and falls back to the theme's media column / older gallery snippets.
        // Scroll UP only (gallery above the viewport), offset for a sticky header.
        // Skip when this picker sits inside a modal (<dialog>) — e.g. the mini-PDP
        // "See it in Action" popup — so a colour change there never scrolls the
        // page behind it. The PDP buybox picker is not in a dialog, so it scrolls
        // exactly as before.
        if (variant.available && !this.closest('dialog')) {
          try {
            const _g = document.querySelector('sai-emblag01, sai-im4x2uig, sai-hn2hxz5f, sai-ty137c1l, .product-information__media, .product-media-gallery, .product-media-container, .product__media')
            if (_g) {
              const _r = _g.getBoundingClientRect()
              if (_r.top < -4) {
                let _off = 0
                document.querySelectorAll('header, sticky-header, [class*="header-section"], [class*="site-header"]').forEach((_el) => {
                  const _cs = getComputedStyle(_el)
                  if (_cs.position === 'fixed' || _cs.position === 'sticky') {
                    const _hr = _el.getBoundingClientRect()
                    if (_hr.top <= 4 && _hr.height > 10 && _hr.height < 220 && _hr.width > 200) _off = Math.max(_off, _hr.bottom)
                  }
                })
                window.scrollTo({ top: Math.max(0, _r.top + window.scrollY - _off - 6), behavior: 'smooth' })
              }
            }
          } catch (e) {}
        }
      }
      this._track(`${FEATURE_SLUG}:select_swatch`, { value, variant_id: variant ? variant.id : null })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && (initPool.product || initPool.bundle || initPool.axis || initPool.buying_options)) { if (!el._adopt(initPool)) { el._painted = true; el.applyVariant({}, initPool) } }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.product) { if (!el._adopt(pool)) { el._applied = true; el.applyVariant({}, pool) } }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiFswfrd01 = { swatchFor }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  // Injected-content rebinding survives the observer's time window (quick-add drawer).
  window.__saiRebind = window.__saiRebind || {}
  window.__saiRebind['fswfrd01'] = bindAll
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
